import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  ContentLabel,
  ContentValue,
  Link,
  LoadingIndicator,
  ModalButton,
  User
} from 'components/common';

import type { GetAccessLogsRes } from 'shared-types/responses';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';

interface Props {
  accessLog: GetAccessLogsRes['accessLogs'][0];
}

const UserDataAccessLogDetails = ({ accessLog }: Props) => {
  const { messages, theme } = useReduxState();

  const [fetchUsers, setFetchUsers] = useState(false);

  const { data, loading } = useApiData({
    url: `/admin/users-admin-emails?selectedUserIds=${JSON.stringify(
      accessLog.affectedUserIds
    )}`,
    onlyIf: accessLog?.affectedUserIds?.length && fetchUsers
  });

  return (
    <ModalButton
      category="lighter"
      modalContentStyle={{ backgroundColor: theme.lightGreyLighter }}
      modalProps={{
        closeButton: true,
        title: messages.eventCalendarEventDetails,
        width: 'wide'
      }}
      onModalHide={() => setFetchUsers(false)}
      onModalShow={() => setFetchUsers(true)}
      size="small"
      text={messages.eventCalendarEventDetails}
    >
      <div className="inner">
        <Box>
          <ContentLabel
            horizontal={true}
            label={messages.adminUserDataAccessLabelAccessBy}
          >
            {typeof accessLog.requestUser === 'string' ? (
              <ContentValue>{accessLog.requestUser}</ContentValue>
            ) : (
              <User enableProfileLink={true} user={accessLog.requestUser} />
            )}
          </ContentLabel>

          <ContentLabel horizontal={true} label={messages.commonTime}>
            {getFormattedDate({
              date: accessLog.date,
              dateFormat: 'dd.MM.y HH:mm'
            })}
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.adminUserDataAccessLabelEndpoint}
          >
            {accessLog.route}
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.adminUserDataAccessLabelRequestBody}
          >
            <pre className="pre">
              <code>
                {JSON.stringify(JSON.parse(accessLog.requestBody), null, 2)}
              </code>
            </pre>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.adminUserDataAccessLabelRequestQuery}
          >
            <pre className="pre">
              <code>
                {JSON.stringify(JSON.parse(accessLog.requestQuery), null, 2)}
              </code>
            </pre>
          </ContentLabel>
        </Box>

        <Box title={messages.commonUsers} padding={0} style={{ marginTop: 15 }}>
          {loading ? (
            <LoadingIndicator />
          ) : data?.users?.length ? (
            <BoxOverflowWrapper>
              <BoxTable>
                <BoxTableHeader>
                  <BoxTableCell></BoxTableCell>
                  <BoxTableCell>{messages.commonUsername}</BoxTableCell>
                  <BoxTableCell>{messages.commonEmail}</BoxTableCell>
                  <BoxTableCell>{messages.usersFirstName}</BoxTableCell>
                  <BoxTableCell>{messages.usersLastName}</BoxTableCell>
                </BoxTableHeader>

                {data.users.map((user) => (
                  <BoxTableRow key={user.id}>
                    <BoxTableCell>
                      <User
                        showNameOrUsername={false}
                        size="small"
                        user={user}
                      />
                    </BoxTableCell>
                    <BoxTableCell>
                      <Link
                        href={`/users/${user.id}`}
                        linkColor="inherit"
                        text={user.username}
                      />
                    </BoxTableCell>
                    <BoxTableCell>{user.email}</BoxTableCell>
                    <BoxTableCell>{user.firstName}</BoxTableCell>
                    <BoxTableCell>{user.lastName}</BoxTableCell>
                  </BoxTableRow>
                ))}
              </BoxTable>
            </BoxOverflowWrapper>
          ) : (
            <div style={{ padding: 20, textAlign: 'center' }}>
              {messages.commonNoUsers}
            </div>
          )}
        </Box>

        <style jsx>{`
          .pre {
            background-color: ${theme.lightGreyLighter};
            border-radius: ${theme.cardBorderRadius};
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            margin: 0;
            padding: 10px;
          }
        `}</style>
      </div>
    </ModalButton>
  );
};

export default UserDataAccessLogDetails;
